/**
 * App — router. Routes per the F0 scope + F4 history:
 *   /login            public
 *   /                 host console (guarded)
 *   /viewer/:token    public viewer page (F2 — outside RouteGuard, no auth)
 *   /history          history list (F4, guarded)
 *   /history/:id      session detail: transcript + audio + export (F4, guarded)
 *   /credentials      provider credentials (guarded)
 */

import { useEffect, type ReactElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { applyTheme, preferredMode } from '@/app/theme';
import { RouteGuard } from '@/app/RouteGuard';
import { LoginScreen } from '@/features/auth/LoginScreen';
import { AuthCallback } from '@/features/auth/AuthCallback';
import { HostConsole } from '@/features/session/HostConsole';
import { ViewerPage } from '@/features/viewer/ViewerPage';
import { CredentialsScreen } from '@/features/credentials/CredentialsScreen';
import { HistoryList } from '@/features/history/HistoryList';
import { SessionDetail } from '@/features/history/SessionDetail';

export function App(): ReactElement {
  useEffect(() => {
    applyTheme(preferredMode());
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/viewer/:token" element={<ViewerPage />} />
        <Route
          path="/"
          element={
            <RouteGuard>
              <HostConsole />
            </RouteGuard>
          }
        />
        <Route
          path="/history"
          element={
            <RouteGuard>
              <HistoryList />
            </RouteGuard>
          }
        />
        <Route
          path="/history/:id"
          element={
            <RouteGuard>
              <SessionDetail />
            </RouteGuard>
          }
        />
        <Route
          path="/credentials"
          element={
            <RouteGuard>
              <CredentialsScreen />
            </RouteGuard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
